import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {useTheme} from "@emotion/react";
import {getPercentage} from "../../../common";
import {temporary_actions} from "../../../redux/slice/TemporaryReducer";
import {useDispatch, useSelector} from "react-redux";
import {useModelContext} from "../../../context/ModelContextProvider";
import {useMobileDetect, useMountEffect} from "../../../utils/Hooks";
import {
    BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY,
    BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE,
    BROADCAST_ON_SUBJECT_SELECT,
    getImageUrl,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import CircleLoading from "../../_common/component/CircleLoading";
import Log from "../../../common/Log";
import AutoPlayButton from "../../_common/component/AutoPlayButton";

export const TYPE_CHAPTER_WRAPPER = 1;
export const TYPE_SUBJECT_LIST_WRAPPER = 2;

/*
    props
    course : {} = 현재 url로 전달된 course_id의 course
    subject_set : [{}, {}, ...] = 현재 course의 모든 subject 배열
 */
const SubjectList = (props) => {
    const dispatch = useDispatch();
    const {broadcast} = useApplicationContext();
    const {course_model, isSuccess, isError} = useModelContext();
    const {is_mobile} = useMobileDetect();

    // state
    const [active_subject, setActiveSubject] = useState({});
    const [attendance_rate, setAttendanceRate] = useState(null);
    const [is_loading, setIsLoading] = useState(false);
    const [can_render, setCanRender] = useState(false);
    const [subjects_by_chapter, setSubjectsByChapter] = useState({});

    // ref
    const selected_chapter_num_set = useRef([]);
    const list_wrapper_ref = useRef({});

    // css style
    const {view} = useComponentStyle(is_mobile);

    useMountEffect(() => {
        updateUi();
    });

    const updateUi = async () => {
        groupedSubjectsByChapter();

        await setFirstUnViewedSubject();
    };

    // 전달받은 subject_set의 chapter별로 구별해서 배열로 묶기 : subjects_by_chapter
    const groupedSubjectsByChapter = useCallback(() => {
        setIsLoading(true);

        props.subject_set.map((subject) => {
            let subject_chapter_num = Number(subject.chapter_description.substring(1, 11).replace(/[^0-9]/g, ""));

            if (subject_chapter_num === 0) {
                subject_chapter_num = "end";
            }

            if (!subjects_by_chapter[subject_chapter_num]) {
                let tmp = {
                    [subject_chapter_num]: [subject],
                };

                return setSubjectsByChapter(Object.assign(subjects_by_chapter, tmp));
            } else {
                return subjects_by_chapter[subject_chapter_num].push(subject);
            }
        });
    }, [props.subject_set, subjects_by_chapter]);

    const getSubjectDetail = useCallback(
        async (subject) => {
            const subject_detail_res = await course_model.getSubjectDetail(props.course.id, subject.id);

            setIsLoading(false);
            if (isSuccess(subject_detail_res)) {
                return subject_detail_res.data.subject;
            } else if (isError(subject_detail_res)) {
                Log.private("getSubjectDetailViewer 에러", subject_detail_res);
                return null;
            }
        },
        [course_model, isSuccess, isError, active_subject]
    );

    // 최초의 강의 = is_viewed : false || subject_set[0]
    const setFirstUnViewedSubject = useCallback(async () => {
        let un_viewed_subject = props.subject_set.find((subject) => !subject.is_viewed);
        if (!un_viewed_subject) {
            un_viewed_subject = props.subject_set[0];
        }

        setActiveSubject(un_viewed_subject);

        const subject_detail = await getSubjectDetail(un_viewed_subject);
        setAttendanceRate(subject_detail.attendance_rate);

        setCanRender(true);
    }, [props.subject_set, getSubjectDetail]);

    useEffect(() => {
        // 페이지 렌더시에 active_subject(현재 subject)의 chapter에 active 클래스 추가
        if (can_render) {
            Object.keys(subjects_by_chapter).map((chapter_num) => {
                subjects_by_chapter[chapter_num].map((subject) => {
                    if (subject.id === active_subject.id) {
                        selected_chapter_num_set.current.push(chapter_num);
                        if (!list_wrapper_ref.current[chapter_num].classList.value.split(" ").includes("active")) {
                            list_wrapper_ref.current[chapter_num].classList.add("active");
                        }
                    }
                });
            });
        }
    }, [active_subject, can_render, dispatch, props.course.id]);

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY], ({value}) => {
            setActiveSubject(value);
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast]);

    useEffect(() => {
        // Player에서 강의가 끝나면 is_viewed체크를 위해 broadcast를 보낸다.(getCourseDetail을 다시 하기엔 로딩이 너무 길음)
        const unlisten = broadcast.listen([BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE], ({value}) => {
            let tmp = {
                ...subjects_by_chapter,
            };
            let active_subject_index;
            let viewed_subject = 0;

            // 수강완료 체크 업데이트
            for (let chapter_num in tmp) {
                active_subject_index = tmp[chapter_num].findIndex((subject) => subject.id === value.id);

                if (active_subject_index >= 0) {
                    tmp[chapter_num][active_subject_index].is_viewed = true;
                    break;
                }
            }
            setSubjectsByChapter({
                ...tmp,
            });

            // 전체 수강률 업데이트
            for (let chapter_num in tmp) {
                tmp[chapter_num].map((subject) => {
                    if (subject.is_viewed) {
                        viewed_subject += 1;
                    }
                });
            }
            setAttendanceRate(viewed_subject / props.subject_set.length);
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast, active_subject, subjects_by_chapter, props.subject_set.length]);

    const addClassNameWithRender = (chapter_num, type) => {
        // 리렌더 시에 펼쳐놓은 chapter를 유지하기 위해 ChapterWrapper에 클래스명 "spread" 부여
        if (
            subjects_by_chapter[chapter_num].includes(active_subject) ||
            selected_chapter_num_set.current.includes(chapter_num)
        ) {
            if (type === TYPE_CHAPTER_WRAPPER) return "spread";
            else if (type === TYPE_SUBJECT_LIST_WRAPPER) return "active";
        } else {
            return null;
        }
    };

    // ChapterWrapper 클릭
    const onChapterSpread = (chapter_num) => {
        // 기존에 펼쳐져 있던 chapter라면 ChapterWrapper에 "spread", SubjectListWrapper에 "active" 클래스 제거
        if (list_wrapper_ref.current[chapter_num].classList.value.split(" ").includes("active")) {
            selected_chapter_num_set.current = selected_chapter_num_set.current.filter(
                (exist) => exist !== chapter_num
            );
            list_wrapper_ref.current[chapter_num].classList.remove("active");
            list_wrapper_ref.current[chapter_num].parentElement.children[0].classList.remove("spread");
        } else {
            // 기존에 펼쳐져 있지 않던 chapter라면 ChapterWrapper에 "spread", SubjectListWrapper에 "active" 클래스 추가
            if (!selected_chapter_num_set.current.includes(chapter_num)) {
                selected_chapter_num_set.current.push(chapter_num);
            }
            list_wrapper_ref.current[chapter_num].classList.add("active");
            list_wrapper_ref.current[chapter_num].parentElement.children[0].classList.add("spread");
        }
    };

    // subject 클릭 -> SubjectViewer로 subject 전달
    const onSubjectClick = (subject) => {
        if (subject.id !== active_subject.id) {
            broadcast.send(BROADCAST_ON_SUBJECT_SELECT, subject);
            setActiveSubject(subject);
        } else {
            return false;
        }
    };

    const getSubjectIndexName = (subject) => {
        // index_name이 "1강", "2강", ...의 형태가 아닌 "번외1" 등의 사례를 대비
        let subject_index_name = subject.index_name.replace(/[^ㄱ-ㅎ|가-힣]/g, "");
        if (subject_index_name === "강") {
            return subject.index_name.replace(/[^0-9]$/g, "");
        } else {
            return subject_index_name;
        }
    };

    return !is_mobile && is_loading ? (
        <div
            style={{
                textAlign: "center",
            }}>
            <CircleLoading />
        </div>
    ) : (
        <>
            <view.TopWrapper>
                <view.SummaryWrapper>
                    <span>총 {props.subject_set.length}개 강의</span>
                    <span>수강률 {getPercentage(attendance_rate)}%</span>
                </view.SummaryWrapper>
                <view.AutoPlayWrapper>
                    <span>연속 재생</span>
                    <AutoPlayButton />
                </view.AutoPlayWrapper>
            </view.TopWrapper>
            <view.ListWrapper>
                {Object.keys(subjects_by_chapter).map((chapter_num, i) => {
                    return (
                        <div key={i}>
                            <view.ChapterWrapper
                                className={addClassNameWithRender(chapter_num, TYPE_CHAPTER_WRAPPER)}
                                onClick={() => onChapterSpread(chapter_num)}>
                                <div className={"chapter-num-box"}>
                                    <div className="chapter-num">CHAPTER {chapter_num}</div>
                                </div>
                                <div className={"chapter-title"}>
                                    {subjects_by_chapter[chapter_num][0].chapter_description.substring(11)}
                                </div>
                                <div className={"chapter-spread-icon-box"}>
                                    <div className="chapter-spread-icon" />
                                </div>
                            </view.ChapterWrapper>
                            <view.SubjectListWrapper
                                className={addClassNameWithRender(chapter_num, TYPE_SUBJECT_LIST_WRAPPER)}
                                ref={(ref) => (list_wrapper_ref.current[chapter_num] = ref)}>
                                {subjects_by_chapter[chapter_num].map((subject, i) => (
                                    <view.SubjectWrapper
                                        key={i}
                                        is_active={subject.id === active_subject.id}
                                        onClick={() => {
                                            onSubjectClick(subject);
                                        }}>
                                        <div className="subject-number">{getSubjectIndexName(subject)}</div>
                                        <div className="subject-title">{subject.title}</div>
                                        <div className="subject-viewed-check">
                                            {subject.is_viewed && <div className="subject-viewed-check-icon" />}
                                        </div>
                                        <div className="subject-duration-time">
                                            {/* @TODO 현수 : null 위치에 강의 별 시간 출력*/}
                                            {subject.id === active_subject.id ? (
                                                <div className="subject-play-icon" />
                                            ) : null}
                                        </div>
                                    </view.SubjectWrapper>
                                ))}
                            </view.SubjectListWrapper>
                        </div>
                    );
                })}
            </view.ListWrapper>
        </>
    );
};

export default SubjectList;

const useComponentStyle = (is_mobile) => {
    const theme = useTheme();
    return {
        view: {
            TopWrapper: styled.div({
                width: "100%",
                marginTop: ".3em",
                height: is_mobile ? "4em" : "3.3em",
                backgroundColor: !is_mobile && theme.colors.lg2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: theme.font_size.f15,
                boxSizing: "border-box",
                borderTop: is_mobile && `2px solid ${theme.colors.lg2}`,
                borderBottom: is_mobile && `2px solid ${theme.colors.lg2}`,
                "@media (max-width: 340px)": {
                    fontSize: theme.font_size.f14,
                },
            }),
            SummaryWrapper: styled.div({
                span: {
                    fontSize: is_mobile && theme.font_size.f16,
                    marginLeft: ".7em",
                },
                "& > span:nth-of-type(1)": {
                    color: is_mobile && "#727272",
                    "@media (max-width: 320px)": {
                        marginLeft: "0",
                    },
                },
                "& > span:nth-of-type(2)": {
                    color: is_mobile && theme.colors.dg1,
                    fontWeight: "600",
                    "@media (max-width: 320px)": {
                        marginLeft: ".3em",
                    },
                },
            }),
            AutoPlayWrapper: styled.div({
                display: "flex",
                alignItems: "center",
                marginRight: "1em",
                "@media (max-width: 320px)": {
                    marginRight: "0",
                },
                span: {
                    fontSize: is_mobile && theme.font_size.f18,
                    marginRight: ".4em",
                    "@media (max-width: 320px)": {
                        fontSize: theme.font_size.f17,
                        marginRight: ".2em",
                    },
                },
            }),
            ListWrapper: styled.ul({
                width: "100%",
                height: "auto",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                "& > div:last-child > ul": {
                    marginBottom: "2em",
                },
            }),
            ChapterWrapper: styled.li({
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "1.8em",
                padding: ".9em 0",
                fontWeight: "700",
                fontSize: is_mobile ? theme.font_size.f16 : theme.font_size.f17,
                cursor: "pointer",
                "& > .chapter-num-box": {
                    minWidth: "5.3em",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: is_mobile && "0 .5em",
                    "@media(max-width: 320px)": {
                        marginLeft: "0",
                    },
                },
                "& .chapter-num": {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "10em",
                    boxSizing: "border-box",
                    border: `2px solid ${theme.colors.dg3}`,
                    padding: ".2em .5em",
                    fontSize: theme.font_size.f14,
                    fontWeight: is_mobile ? "600" : "500",
                    "@media (max-width: 320px)": {
                        fontSize: theme.font_size.f14,
                        padding: ".2em .3em",
                    },
                },
                "& > .chapter-title": {
                    textOverflow: "ellipsis",
                    paddingLeft: ".4em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    flex: 1,
                    fontSize: is_mobile ? theme.font_size.f16 : theme.font_size.f15,
                    "@media (max-width: 320px)": {
                        fontSize: theme.font_size.f14,
                        paddingLeft: "0",
                    },
                },
                "& > .chapter-spread-icon-box": {
                    width: "10%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                },
                "& .chapter-spread-icon": {
                    width: "1em",
                    height: "100%",
                    background: `url(${getImageUrl(
                        "image/common/ic_arrow_down_grey.png"
                    )}) center center/contain no-repeat`,
                    transition: "transform .2s",
                },
                "&.spread .chapter-spread-icon": {
                    transform: "rotate(180deg)",
                },
            }),
            SubjectListWrapper: styled.ul({
                width: "100%",
                boxSizing: "border-box",
                padding: !is_mobile && "0 .5em",
                maxHeight: "0",
                overflow: "hidden",
                borderRadius: !is_mobile && ".8em",
                transition: "max-height .5s ease-out",
                "&.active": {
                    maxHeight: "600px",
                },
            }),
            SubjectWrapper: styled.li((props) => ({
                width: "100%",
                height: "2.8em",
                boxSizing: "border-box",
                display: "flex",
                backgroundColor: props.is_active ? theme.colors.bearu_primary : theme.colors.w,
                borderRadius: !is_mobile && ".8em",
                fontSize: theme.font_size.f16,
                cursor: "pointer",
                "@media (max-width: 320px)": {
                    fontSize: theme.font_size.f14,
                    height: "3.2em",
                },
                "& > .subject-number": {
                    textAlign: "center",
                    width: "10%",
                    height: "100%",
                    color: props.is_active ? theme.colors.w : theme.colors.dg4,
                    lineHeight: "2.8em",
                    "@media (max-width: 320px)": {
                        lineHeight: "3.2em",
                    },
                },
                "& > .subject-title": {
                    borderBottom: props.is_active ? "none" : `1px solid ${theme.colors.lg1}`,
                    boxSizing: "border-box",
                    width: "70%",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontWeight: "400",
                    height: "100%",
                    lineHeight: "2.8em",
                    color: props.is_active ? theme.colors.w : "inherit",
                    "@media (max-width: 320px)": {
                        lineHeight: "3.2em",
                    },
                },
                "& > .subject-viewed-check": {
                    width: "10%",
                    borderBottom: props.is_active ? "none" : `1px solid ${theme.colors.lg1}`,
                    boxSizing: "border-box",
                    height: "100%",
                    "& .subject-viewed-check-icon": {
                        width: "2.2em",
                        height: "100%",
                        margin: "auto",
                        background: `url(${getImageUrl(
                            "image/common/ic_check_linecircle_x3.png"
                        )}) center center/contain no-repeat`,
                    },
                },
                "& > .subject-duration-time": {
                    textAlign: "center",
                    width: "10%",
                    borderBottom: props.is_active ? "none" : `1px solid ${theme.colors.lg1}`,
                    boxSizing: "border-box",
                    height: "100%",
                    color: theme.colors.dg4,
                    fontSize: theme.font_size.f14,
                    "& .subject-play-icon": {
                        width: "2.2em",
                        height: "100%",
                        margin: "auto",
                        background: `url(${getImageUrl(
                            "image/common/ic_play_x3.png"
                        )}) center center/contain no-repeat`,
                    },
                },
            })),
        },
    };
};
